import React from 'react';
import TeacherTimetable, { type PreviewEvent, type TeacherTimetableSelectEvent } from './TeacherTimetable';
import type { TeacherAvailabilitySlot } from '../../lib/personen';
import { useScheduleViewportDensity } from '../../hooks/useScheduleViewportDensity';

type WizardSlotSelection = {
  date?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

interface WizardScheduleGridProps {
  teacherId: number | string;
  initialViewMode?: 'day' | 'week';
  height?: number;
  previewEvent?: PreviewEvent | null;
  availabilitySlots?: TeacherAvailabilitySlot[];
  selectedSlot?: { date?: string; dayOfWeek: number; startTime: string; endTime?: string } | null;
  referenceDate?: Date;
  onWeekChange?: (date: Date) => void;
  onSelectSlot: (slot: WizardSlotSelection) => void;
  onSelectEvent?: (event: TeacherTimetableSelectEvent) => void;
  allowOccupiedSelection?: boolean;
  /** Termine, die hier nichts zu suchen haben — etwa die Probestunde zur Anmeldung. */
  hideEventIds?: readonly string[];
}

const WizardScheduleGrid: React.FC<WizardScheduleGridProps> = ({
  teacherId,
  initialViewMode = 'week',
  height = 420,
  previewEvent,
  availabilitySlots,
  selectedSlot,
  referenceDate,
  onWeekChange,
  onSelectSlot,
  onSelectEvent,
  allowOccupiedSelection = true,
  hideEventIds,
}) => {
  const { gridMaxHeight } = useScheduleViewportDensity();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = React.useState(0);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const applyHeight = () => {
      const next = Math.max(0, Math.floor(node.clientHeight));
      setContainerHeight(next);
    };

    applyHeight();
    const observer = new ResizeObserver(() => applyHeight());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const maxHeight = Math.max(280, gridMaxHeight - 180);
  const containerSafeHeight = containerHeight > 0 ? Math.max(280, containerHeight) : 0;
  const effectiveHeight =
    containerHeight > 0
      ? Math.min(containerSafeHeight, maxHeight)
      : Math.max(280, Math.min(height, maxHeight));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={containerRef} className="flex min-h-0 flex-1 flex-col">
        <TeacherTimetable
          teacherId={teacherId}
          referenceDate={referenceDate}
          availabilitySlots={availabilitySlots}
          displayMode={initialViewMode}
          showNavigation={false}
          showTodayButton={false}
          allowOccupiedSelection={allowOccupiedSelection}
          height={effectiveHeight}
          selectedSlot={
            selectedSlot
              ? {
                  dayOfWeek: selectedSlot.dayOfWeek,
                  startTime: selectedSlot.startTime.slice(0, 5),
                }
              : null
          }
          previewEvent={previewEvent}
          hideEventIds={hideEventIds}
          onWeekChange={onWeekChange}
          onSelectSlot={(slot) => onSelectSlot(slot)}
          onSelectEvent={onSelectEvent}
        />
      </div>
    </div>
  );
};

export type { WizardSlotSelection };
export default WizardScheduleGrid;

